import env from '@travel/env';

export const apiPath = {
  fetchToken: {
    id: 'fetch-auth-code',
    pathname: '/consumer/auth-endusers/member-token/current-login-token',
  },
  refreshToken: {
    id: 'refresh-auth-code',
    pathname: '/consumer/auth-endusers/member-refresh/current-refresh-token',
  },
  logoutToken: {
    id: 'logout-auth-code',
    pathname: '/consumer/auth-endusers/member-token/current-logout-token',
  },
  reviewList: {
    id: 'post-review-list',
    pathname: '/consumer/reviews/review/list',
  },
  providerFilters: {
    id: 'provider-filters',
    pathname: (providerId: string) => `/consumer/reviews/provider/${providerId}/filters`,
    regexp: /\/consumer\/reviews\/provider\/\d+\/filters$/,
  },
  getUserProfile: {
    id: 'get-user-profile',
    pathname: '/consumer/reviews/user/profile',
  },
  profileById: {
    id: 'get-profile-by-id',
    pathname: (userId: string) => `/consumer/reviews/user/profile/${userId}`,
    regexp: /\/consumer\/reviews\/user\/profile\/[a-zA-Z0-9._-]+$/,
  },
  setViewCount: {
    id: 'set-view-count',
    pathname: '/consumer/reviews/review/views',
  },
  providerReviewSummary: {
    id: 'post-provider-review-summary',
    pathname: (providerId: number) => `/consumer/reviews/provider/${providerId}/summary`,
    regexp: /\/consumer\/reviews\/provider\/[0-9]+\/summary$/,
  },
  mediaGallery: {
    id: 'get-media-gallery',
    pathname: (providerId: number) => `/consumer/reviews/provider/${providerId}/media-gallery`,
    regexp: /\/consumer\/reviews\/provider\/[0-9]+\/media-gallery$/,
  },
  getReviewConfig: {
    id: 'get-review-config',
    pathname: '/consumer/reviews/review-form',
  },
  postReview: {
    id: 'post-review',
    pathname: () => '/consumer/reviews/review',
    regexp: /\/consumer\/reviews\/review$/,
  },
  editReview: {
    id: 'edit-review',
    pathname: (reviewId: string) => `/consumer/reviews/review/${reviewId}`,
    regexp: /\/consumer\/review\/\d+$/,
  },
  setLike: {
    id: 'set-like',
    pathname: (reviewId: string) => `/consumer/reviews/review/${reviewId}/likes`,
    regexp: /\/consumer\/reviews\/review\/\d+\/likes$/,
  },
  mediaConfig: {
    id: 'media-config',
    pathname: env('UNIVERSAL_MEDIA_CONFIG_URL'),
  },
  postMedia: {
    id: 'post-media',
    pathname: '/media-api/media',
  },
  deleteMedia: {
    id: 'delete-media',
    pathname: (mediaId: string) => `/media-api/media/${mediaId}`,
    regexp: /\/media-api\/media\/.+$/,
  },
  generateTitle: {
    id: 'ai-title-generate',
    pathname: '/consumer/reviews/review/title-generation',
  },
  reviewAnonymity: {
    id: 'review-anonymity',
    pathname: (reviewId: string) => `/consumer/reviews/review/${reviewId}/anonymity`,
    regexp: /\/consumer\/reviews\/review\/\d+\/anonymity$/,
  },
  reviewDelete: {
    id: 'review-delete',
    pathname: (reviewId: string) => `/consumer/reviews/review/${reviewId}`,
    regexp: /\/consumer\/reviews\/review\/\d+$/,
  },
  reviewReport: {
    id: 'review-report',
    pathname: (reviewId: string) => `/consumer/reviews/review/${reviewId}/report`,
    regexp: /\/consumer\/reviews\/review\/\d+\/report$/,
  },
  snsList: {
    id: 'get-SNSDefault-list',
    pathname: '/consumer/reviews/user/sns',
  },
  aiTitleConfig: {
    id: 'get-AI-title-config',
    pathname: env('UNIVERSAL_AI_TITLE_CONFIG_URL'),
  },
  commonHeader: {
    id: 'provider-common-header',
    pathname: (providerId: number) => `/provider-page/header/${providerId}`,
    regexp: /\/provider-page\/header\/\d+$/,
  },
  getFavoriteListStatus: {
    id: 'get-favorite-list-status',
    pathname: '/bookmark-domestic/check',
  },
  favoriteList: {
    id: 'favorite-list',
    pathname: '/bookmark-domestic/',
  },
} as const;

export type ApiPathId = (typeof apiPath)[keyof typeof apiPath]['id'];

export type ApiPathMatcher = {
  id: ApiPathId;
} & (
  | {
      pathname: string;
      regexp?: never;
    }
  | {
      pathname: (...args: (string | number)[]) => string;
      regexp: RegExp;
    }
);

type ErrorMapping = Record<'redirect' | 'popup' | 'inline', number[]>;

type MethodSpecificErrors = {
  GET?: ErrorMapping;
  POST?: ErrorMapping;
  PATCH?: ErrorMapping;
  PUT?: ErrorMapping;
  DELETE?: ErrorMapping;
  default?: ErrorMapping; // Fallback for unspecified methods
};

export const apiPathMap: Map<ApiPathId, MethodSpecificErrors> = new Map([
  [
    apiPath.reviewList.id,
    {
      default: {
        redirect: [400, 401, 410, 500],
        popup: [],
        inline: [],
      },
    },
  ],
  [
    apiPath.providerFilters.id,
    {
      default: {
        redirect: [],
        popup: [500],
        inline: [],
      },
    },
  ],
  [
    apiPath.getUserProfile.id,
    {
      PATCH: {
        redirect: [500],
        popup: [401, 403, 404],
        inline: [400],
      },
      default: {
        redirect: [401, 409, 500],
        popup: [],
        inline: [],
      },
    },
  ],
  [
    apiPath.providerReviewSummary.id,
    {
      default: {
        redirect: [500],
        popup: [],
        inline: [],
      },
    },
  ],
  [
    apiPath.mediaGallery.id,
    {
      default: {
        redirect: [400, 401, 410, 500],
        popup: [],
        inline: [],
      },
    },
  ],
  [
    apiPath.setLike.id,
    {
      default: {
        redirect: [],
        popup: [400, 401, 404, 409, 500],
        inline: [],
      },
    },
  ],
  [
    apiPath.profileById.id,
    {
      default: {
        redirect: [400, 401, 404, 500],
        popup: [],
        inline: [410],
      },
    },
  ],

  [
    apiPath.getReviewConfig.id,
    {
      default: {
        redirect: [400, 401, 403, 404, 409, 500],
        popup: [],
        inline: [],
      },
    },
  ],
  [
    apiPath.postReview.id,
    {
      default: {
        redirect: [],
        popup: [400, 401, 403, 404, 500],
        inline: [],
      },
    },
  ],
  [
    apiPath.editReview.id,
    {
      default: {
        redirect: [],
        popup: [400, 401, 403, 404, 500],
        inline: [],
      },
    },
  ],
  [
    apiPath.postMedia.id,
    {
      default: {
        redirect: [],
        popup: [400, 401, 403, 404, 408, 409, 410, 500],
        inline: [],
      },
    },
  ],
  [
    apiPath.deleteMedia.id,
    {
      default: {
        redirect: [],
        popup: [400, 401, 403, 404, 409, 410, 500],
        inline: [],
      },
    },
  ],
  [
    apiPath.reviewDelete.id,
    {
      default: {
        redirect: [],
        popup: [400, 401, 403, 404, 409, 410, 500],
        inline: [],
      },
    },
  ],
  [
    apiPath.reviewAnonymity.id,
    {
      default: {
        redirect: [],
        popup: [400, 401, 403, 404, 500],
        inline: [],
      },
    },
  ],
  [
    apiPath.reviewReport.id,
    {
      default: {
        redirect: [],
        popup: [400, 401, 403, 404, 409, 500],
        inline: [],
      },
    },
  ],
  [
    apiPath.snsList.id,
    {
      PATCH: {
        redirect: [500],
        popup: [400, 401, 403, 404],
        inline: [],
      },
      default: {
        redirect: [500],
        popup: [],
        inline: [],
      },
    },
  ],
  [
    apiPath.commonHeader.id,
    {
      default: {
        redirect: [400, 401, 403, 404, 500],
        popup: [],
        inline: [],
      },
    },
  ],
]);

export const EXCEPTION_PATH_LIST = {
  503: [
    apiPath.aiTitleConfig.pathname,
    apiPath.generateTitle.pathname,
    apiPath.getFavoriteListStatus.pathname,
    apiPath.favoriteList.pathname,
    apiPath.mediaConfig.pathname,
  ],
  408: [
    apiPath.postMedia.pathname,
    apiPath.generateTitle.pathname,
    apiPath.aiTitleConfig.pathname,
    apiPath.getFavoriteListStatus.pathname,
    apiPath.favoriteList.pathname,
    apiPath.mediaConfig.pathname,
  ],
} as const;
